<template>
  <div class="flex-1 relative z-0 flex overflow-hidden">
    <main
      scroll-region
      class="
        flex-1
        relative
        z-0
        overflow-y-auto
        focus:outline-none
        xl:order-last
      "
      v-if="equipment"
      tabindex="0"
    >
      <nav
        class="flex items-start px-4 py-3 sm:px-6 lg:px-8 xl:hidden"
        aria-label="Breadcrumb"
      >
        <Link
          :data="{ search: $page.props.filters.search }"
          preserve-scroll
          preserve-state
          :href="route('equipment.index')"
          class="
            inline-flex
            items-center
            space-x-3
            text-sm
            font-medium
            text-gray-900
          "
        >
          <ChevronLeftIcon class="-ml-2 h-5 w-5 text-gray-400" />
          <span>{{ t("back_directory") }}</span>
        </Link>
      </nav>

      <equipment-detail
        :tab="tab"
        :equipmentCalibrations="equipmentCalibrations"
        :equipmentRevisions="equipmentRevisions"
        :equipment="equipment"
      />
    </main>
    <directory
      :class="{ 'hidden xl:flex': equipment }"
      :equipments="equipments"
      :totalEquipments="totalEquipments"
      :sortedEquipments="sortedEquipments"
    />

    <teleport to="#modals">
      <equipment-creation
        @toggleVisibility="creatingEquipment = !creatingEquipment"
        :on="creatingEquipment"
      />
      <equipment-calibration-creation
        v-if="equipment"
        @toggleVisibility="
          creatingEquipmentCalibration = !creatingEquipmentCalibration
        "
        :on="creatingEquipmentCalibration"
        :equipment="equipment"
      />
      <equipment-calibration-edit
        v-if="equipmentCalibration"
        @toggleVisibility="
          editingEquipmentCalibration = !editingEquipmentCalibration
        "
        :on="editingEquipmentCalibration"
        :equipmentCalibration="equipmentCalibration"
        :equipment="equipment"
      />

      <equipment-revision-creation
        v-if="equipment"
        @toggleVisibility="
          creatingEquipmentRevision = !creatingEquipmentRevision
        "
        :on="creatingEquipmentRevision"
        :equipment="equipment"
      />
      <equipment-revision-edit
        v-if="equipmentRevision"
        @toggleVisibility="editingEquipmentRevision = !editingEquipmentRevision"
        :on="editingEquipmentRevision"
        :equipmentRevision="equipmentRevision"
        :equipment="equipment"
      />

      <equipment-status-change-creation
        v-if="equipment && equipmentStatuses"
        @toggleVisibility="
          creatingEquipmentStatusChange = !creatingEquipmentStatusChange
        "
        :equipmentStatuses="equipmentStatuses"
        :on="creatingEquipmentStatusChange"
        :equipment="equipment"
      />

      <equipment-status-change-edit
        v-if="equipmentStatusChange && equipmentStatuses"
        @toggleVisibility="
          editingEquipmentStatusChange = !editingEquipmentStatusChange
        "
        :equipmentStatuses="equipmentStatuses"
        :on="editingEquipmentStatusChange"
        :equipmentStatusChange="equipmentStatusChange"
        :equipment="equipment"
      />
    </teleport>
  </div>
</template>

<script>
import { ChevronLeftIcon } from "@heroicons/vue/outline";
import AppLayout from "./../Layouts/AppLayout";
import Directory from "./Equipments/Directory";
import EquipmentDetail from "./Equipments/EquipmentDetail";
import EquipmentCreation from "./Equipments/EquipmentCreation";
import EquipmentCalibrationCreation from "./Equipments/EquipmentCalibrationCreation";
import EquipmentStatusChangeCreation from "./Equipments/EquipmentStatusChangeCreation";
import EquipmentStatusChangeEdit from "./Equipments/EquipmentStatusChangeEdit";
import EquipmentCalibrationEdit from "./Equipments/EquipmentCalibrationEdit";
import EquipmentRevisionCreation from "./Equipments/EquipmentRevisionCreation";
import EquipmentRevisionEdit from "./Equipments/EquipmentRevisionEdit";
import { useI18n } from "vue-i18n";

export default {
  layout: AppLayout,

  props: {
    equipment: Object,
    equipments: Object,
    equipmentCalibration: Object,
    equipmentRevision: Object,
    equipmentStatusChange: Object,
    totalEquipments: Number,
    equipmentCalibrations: Object,
    equipmentRevisions: Object,
    equipmentStatuses: Array,
    filters: Object,
    tab: String,
    openCreateEquipment: {
      type: Boolean,
      default: false,
    },
    openCreateEquipmentCalibration: {
      type: Boolean,
      default: false,
    },
    openEditEquipmentCalibration: {
      type: Boolean,
      default: false,
    },
    openCreateEquipmentRevision: {
      type: Boolean,
      default: false,
    },
    openEditEquipmentRevision: {
      type: Boolean,
      default: false,
    },
    openCreateEquipmentStatusChange: {
      type: Boolean,
      default: false,
    },
    openEditEquipmentStatusChange: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    AppLayout,
    Directory,
    EquipmentDetail,
    EquipmentCreation,
    EquipmentCalibrationCreation,
    EquipmentCalibrationEdit,
    EquipmentRevisionCreation,
    EquipmentRevisionEdit,
    EquipmentStatusChangeCreation,
    EquipmentStatusChangeEdit,
    ChevronLeftIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  created() {
    this.sortEquipments();

    if (this.openCreateEquipment) {
      this.creatingEquipment = true;
    }

    if (this.openCreateEquipmentCalibration) {
      this.creatingEquipmentCalibration = true;
    }

    if (this.openCreateEquipmentRevision) {
      this.creatingEquipmentRevision = true;
    }

    if (this.openCreateEquipmentStatusChange) {
      this.creatingEquipmentStatusChange = true;
    }

    if (this.openEditEquipmentCalibration) {
      this.editingEquipmentCalibration = true;
    }

    if (this.openEditEquipmentRevision) {
      this.editingEquipmentRevision = true;
    }

    if (this.openEditEquipmentStatusChange) {
      this.editingEquipmentStatusChange = true;
    }
  },

  data() {
    return {
      sortedEquipments: {},
      creatingEquipment: false,
      creatingEquipmentCalibration: false,
      editingEquipmentCalibration: false,
      creatingEquipmentRevision: false,
      editingEquipmentRevision: false,
      creatingEquipmentStatusChange: false,
      editingEquipmentStatusChange: false,
    };
  },

  methods: {
    sortEquipments() {
      this.sortedEquipments = {};
      this.equipments.data.forEach((equipment) => {
        if (!(equipment.identifier[0] in this.sortedEquipments)) {
          this.sortedEquipments[equipment.identifier[0]] = [];
        }
        this.sortedEquipments[equipment.identifier[0]].push(equipment);
      });
    },
  },

  watch: {
    equipments: function (equipments) {
      this.sortEquipments();
    },
    openEditEquipmentCalibration: function (openEditEquipmentCalibration) {
      if (openEditEquipmentCalibration) {
        this.editingEquipmentCalibration = true;
      }
    },
    openEditEquipmentRevision: function (openEditEquipmentRevision) {
      if (openEditEquipmentRevision) {
        this.editingEquipmentRevision = true;
      }
    },
    openEditEquipmentStatusChange: function (openEditEquipmentStatusChange) {
      if (openEditEquipmentStatusChange) {
        this.editingEquipmentStatusChange = true;
      }
    },
  },
};
</script>

<i18n>
{
  "es": {
    "back_directory": "Directorio de equipos de medición"
  }
}
</i18n>